/*
 * Header search overlay (KRAKEN-5107 / KRAKEN-4629)
 * -----------------------------------------------------------------------------
 * One overlay under the header, driven by the exposed search fields (desktop right
 * cluster, mobile row). Three views: `default` before typing, `results` from
 * sections/predictive-search.liquid, and `empty` when nothing matched.
 *
 * Only one of search, the mobile menu drawer and the desktop mega menu is open at a
 * time: opening search closes the other two, and opening either of them closes search.
 * Listeners are delegated from the document so a theme-editor re-render of the header
 * does not break the fields.
 */
class SearchOverlay extends HTMLElement {
  constructor() {
    super();
    // Below two characters the default view stays up, so one stray keystroke does not
    // swap the popular searches for a near-random result set.
    this.minQueryLength = 2;
    this.debounceMs = 250;
    this.animationMs = 250;
    this.cache = new Map();
    this.isOpen = false;
    this.activeInput = null;
    this.lastQuery = null;
    this.suppressOpen = false;
    this.fetchController = null;
    this.debounceId = 0;
    this.closeTimer = 0;
  }

  connectedCallback() {
    this.header = this.closest('sticky-header') || document.querySelector('sticky-header');
    this.panel = this.querySelector('[data-search-panel]');
    this.status = this.querySelector('[data-search-status]');
    this.views = new Map();
    this.querySelectorAll('[data-search-view]').forEach((view) => this.views.set(view.dataset.searchView, view));
    this.predictiveEnabled = this.dataset.predictive === 'true';
    this.desktopQuery = window.matchMedia('(min-width: 990px)');

    if (!this.header || !this.panel) return;

    this.listeners = new AbortController();
    const { signal } = this.listeners;

    document.addEventListener('focusin', this.onFocusIn, { signal });
    document.addEventListener('input', this.onInput, { signal });
    // Capture: the overlay settles outside clicks, Escape and empty submits before the
    // header's own handlers see them.
    document.addEventListener('click', this.onClick, { signal, capture: true });
    document.addEventListener('keydown', this.onKeydown, { signal, capture: true });
    document.addEventListener('submit', this.onSubmit, { signal, capture: true });
    // `toggle` does not bubble; capture on the document sees a mega menu or the menu
    // drawer opening by keyboard too.
    document.addEventListener('toggle', this.onToggle, { signal, capture: true });
    window.addEventListener('resize', this.onResize, { signal, passive: true });
    this.desktopQuery.addEventListener('change', this.onBreakpointChange, { signal });
  }

  disconnectedCallback() {
    this.listeners?.abort();
    this.fetchController?.abort();
    this.clearDebounce();
    this.clearCloseTimer();
    if (this.isOpen) this.unlockPage();
  }

  onFocusIn = (event) => {
    const input = event.target.closest?.('[data-header-search-input]');

    if (input) {
      if (!this.suppressOpen) this.activate(input);
      return;
    }

    if (!this.isOpen || this.isInside(event.target)) return;

    this.close();
  };

  onInput = (event) => {
    const input = event.target.closest?.('[data-header-search-input]');
    if (!input) return;

    this.activate(input);
  };

  onClick = (event) => {
    const clear = event.target.closest?.('[data-header-search-clear]');

    if (clear) {
      this.onClear(event, clear);
      return;
    }

    if (!this.isOpen) {
      // Re-opens after Escape without having to leave the field and come back.
      const input = event.target.closest?.('[data-header-search-input]');
      if (input) this.activate(input);
      return;
    }

    if (event.target.closest?.('[data-search-scrim]')) {
      // Closing on click (not pointerdown) keeps the scrim under this click, so the tap
      // that dismisses search cannot land on whatever the page has underneath.
      event.preventDefault();
      this.close();
      return;
    }

    if (this.isInside(event.target)) {
      this.guardCardClick(event);
      return;
    }

    // The burger, a mega menu item, the bag: close search and let the click carry on,
    // so the other control opens in the same tap.
    this.close();
  };

  // Clearing a typed query returns to the default view and keeps focus so the shopper
  // can retype; with nothing typed the same button dismisses search.
  onClear(event, button) {
    event.preventDefault();
    const input = button.closest('[data-header-search]')?.querySelector('[data-header-search-input]');

    if (input && input.value !== '') {
      input.value = '';
      input.focus();
      this.activate(input);
      return;
    }

    this.close();
    input?.blur();
  }

  onKeydown = (event) => {
    if (!this.isOpen) return;

    if (event.key === 'Escape') {
      // Stop the Escape here so it neither clears the search field nor travels on to
      // the header menus.
      event.preventDefault();
      event.stopPropagation();

      const input = this.activeInput;
      this.close();

      // Returning focus to the field must not reopen what Escape just dismissed.
      this.suppressOpen = true;
      input?.focus();
      window.setTimeout(() => {
        this.suppressOpen = false;
      });
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') this.moveFocus(event);
  };

  onSubmit = (event) => {
    const form = event.target.closest?.('[data-header-search]');
    if (!form) return;

    const input = form.querySelector('[data-header-search-input]');
    if (!input || input.value.trim() === '') event.preventDefault();
  };

  onToggle = (event) => {
    const details = event.target;
    if (!this.isOpen || !(details instanceof HTMLDetailsElement) || !details.open) return;
    if (!details.closest('header-menu, header-drawer')) return;

    this.close();
  };

  onResize = () => {
    if (this.isOpen) this.measure();
  };

  onBreakpointChange = () => {
    this.close();
  };

  activate(input) {
    this.activeInput = input;
    if (!this.isOpen) this.open();

    this.syncClearButtons();
    this.requestResults(input.value);
  }

  open() {
    this.clearCloseTimer();
    this.isOpen = true;

    this.closeOtherOverlays();
    this.measure();
    this.panel.hidden = false;
    this.lockPage();
    this.header.classList.add('header-wrapper--search-open');
    this.setHeaderIconsInert(true);
    this.activeInput?.setAttribute('aria-expanded', 'true');
    window.dataLayerUtils?.send?.('search_modal_opened');

    if (this.prefersReducedMotion()) {
      this.classList.add('search-overlay--open');
      return;
    }

    // Force layout so the panel animates in from its closed state rather than
    // appearing already open.
    this.panel.getBoundingClientRect();
    requestAnimationFrame(() => {
      if (this.isOpen) this.classList.add('search-overlay--open');
    });
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.fetchController?.abort();
    this.fetchController = null;
    this.clearDebounce();
    this.panel.removeAttribute('aria-busy');
    this.classList.remove('search-overlay--open');
    this.header.classList.remove('header-wrapper--search-open');
    this.setHeaderIconsInert(false);
    this.unlockPage();
    this.syncClearButtons();
    document
      .querySelectorAll('[data-header-search-input]')
      .forEach((input) => input.setAttribute('aria-expanded', 'false'));

    if (this.prefersReducedMotion()) {
      this.finishClose();
      return;
    }

    this.closeTimer = window.setTimeout(() => this.finishClose(), this.animationMs);
  }

  finishClose() {
    this.clearCloseTimer();
    if (this.isOpen) return;

    this.panel.hidden = true;
    // The next open re-renders from the field's value (from cache when possible).
    this.lastQuery = null;
  }

  // One overlay at a time under the header.
  closeOtherOverlays() {
    const drawer = document.querySelector('header-drawer');
    const drawerDetails = drawer?.querySelector('details');

    if (drawerDetails?.hasAttribute('open') && typeof drawer.closeMenuDrawer === 'function') {
      // closeMenuDrawer returns focus to its second argument; hand it the search field so
      // focus stays where the shopper just tapped.
      drawer.closeMenuDrawer(new Event('search-open'), this.activeInput);
      drawerDetails.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    }

    document.querySelectorAll('header-menu').forEach((menu) => {
      if (menu.querySelector('details[open]') && typeof menu.close === 'function') menu.close();
    });
  }

  requestResults(rawValue) {
    const query = (rawValue || '').trim();
    this.clearDebounce();

    if (!this.predictiveEnabled || query.length < this.minQueryLength) {
      this.fetchController?.abort();
      this.fetchController = null;
      this.lastQuery = null;
      this.panel.removeAttribute('aria-busy');
      this.showView('default');
      this.setStatus('');
      return;
    }

    // Back to the query already on screen — but a request for text the shopper has just
    // deleted may still be in flight, and rendering it would show stale results.
    if (query === this.lastQuery) {
      this.fetchController?.abort();
      this.fetchController = null;
      this.panel.removeAttribute('aria-busy');
      return;
    }

    this.panel.setAttribute('aria-busy', 'true');
    this.debounceId = window.setTimeout(() => {
      this.debounceId = 0;
      this.fetchResults(query);
    }, this.debounceMs);
  }

  async fetchResults(query) {
    const cached = this.cache.get(query);

    if (cached !== undefined) {
      this.fetchController?.abort();
      this.fetchController = null;
      this.lastQuery = query;
      this.renderResults(cached, query);
      return;
    }

    this.fetchController?.abort();
    this.fetchController = new AbortController();

    try {
      const response = await fetch(this.buildUrl(query), { signal: this.fetchController.signal });
      if (!response.ok) throw new Error(response.status);

      const markup = await response.text();
      // Cache only a response that carries the results root: anything else (an error page,
      // another theme's section) must not be remembered as this query's results.
      if (!this.parseResults(markup)) throw new Error('Unexpected predictive search response');

      this.cache.set(query, markup);
      if (!this.isOpen) return;

      this.lastQuery = query;
      this.renderResults(markup, query);
    } catch (error) {
      if (error.name === 'AbortError') return;
      // Offline or a server error: keep the view that is on screen.
      this.panel.removeAttribute('aria-busy');
      console.error(error);
    }
  }

  buildUrl(query) {
    const url = new URL(window.routes?.predictive_search_url || '/search/suggest', window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('section_id', this.dataset.resultsSection || 'predictive-search');
    url.searchParams.set('resources[type]', 'query,product,collection,page');
    url.searchParams.set('resources[limit]', '10');
    // Without this Shopify splits the limit across all types, and a query with many
    // suggestions leaves room for only two or three products.
    url.searchParams.set('resources[limit_scope]', 'each');
    return url.toString();
  }

  parseResults(markup) {
    return new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-predictive-results]');
  }

  renderResults(markup, query) {
    this.panel.removeAttribute('aria-busy');

    const incoming = this.parseResults(markup);
    const resultsView = this.views.get('results');
    if (!incoming || !resultsView) return;

    const liveRegion = incoming.querySelector('[data-search-live-region]');
    const statusText = liveRegion?.textContent.trim() || '';

    if (Number(incoming.dataset.resultCount || 0) === 0) {
      this.renderEmptyMessage(query);
      this.showView('empty');
      this.setStatus(statusText);
      return;
    }

    // Products but no suggestions, collections or pages (a misspelling Shopify still matches,
    // such as "pjmas"): keep the left column the frames draw, holding the ideas from before
    // typing, instead of stretching the products across the panel.
    if (!incoming.querySelector('.search-overlay__aside')) {
      const ideas = this.views.get('default')?.querySelector('.search-overlay__aside');
      if (ideas?.children.length) {
        incoming.prepend(ideas.cloneNode(true));
        incoming.classList.remove('search-overlay__layout--products-only');
      }
    }

    resultsView.replaceChildren(incoming);
    this.showView('results');
    this.setStatus(statusText);
  }

  renderEmptyMessage(query) {
    const message = this.querySelector('[data-search-empty-message]');
    if (!message) return;

    // The term goes in as a text node, never as markup.
    const [before, after = ''] = (this.dataset.emptyTemplate || '').split('[terms]');
    message.replaceChildren(document.createTextNode(before), document.createTextNode(query), document.createTextNode(after));
  }

  showView(name) {
    const current = this.views.get(name);
    if (!current) return;

    if (current.hidden) this.panel.scrollTop = 0;
    this.views.forEach((view, viewName) => {
      view.hidden = viewName !== name;
    });
  }

  setStatus(text) {
    if (this.status) this.status.textContent = text;
  }

  // Arrow keys walk the links of the visible view (the WCAG audit relied on arrows in the
  // old dropdown); Tab keeps working as normal. ArrowUp from the first link returns to the
  // field.
  moveFocus(event) {
    const view = [...this.views.values()].find((candidate) => !candidate.hidden);
    if (!view) return;

    const links = [...view.querySelectorAll('[data-search-focusable], a[id^="CardLink-"]')].filter(
      (link) => link.offsetParent !== null
    );
    if (!links.length) return;

    const index = links.indexOf(document.activeElement);
    const fromField = document.activeElement === this.activeInput;
    if (index === -1 && !fromField) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      links[Math.min(index + 1, links.length - 1)].focus();
      return;
    }

    if (index === -1) return;
    event.preventDefault();
    if (index === 0) {
      this.activeInput?.focus();
    } else {
      links[index - 1].focus();
    }
  }

  // Product cards are one big link. global.js stops its wishlist, swatch and quick-add
  // buttons from navigating, but only for cards present at DOMContentLoaded; results are
  // injected later, so the overlay applies the same guard itself.
  guardCardClick(event) {
    const card = event.target.closest('a[id^="CardLink-"]');
    if (!card) return;

    const control = event.target.closest(
      'button, input, select, textarea, [role="button"], quick-add-opener, .card__wishlist-add, .colour-swatches__swatch, .colour-swatch-button'
    );
    if (control && card.contains(control)) event.preventDefault();
  }

  isInside(target) {
    if (!(target instanceof Element)) return false;

    return Boolean(target.closest('[data-search-panel]') || target.closest('[data-header-search]'));
  }

  measure() {
    const headerBottom = Math.max(0, this.header.getBoundingClientRect().bottom);
    this.style.setProperty('--search-overlay-top', `${headerBottom}px`);
    this.style.setProperty('--search-viewport-height', `${window.innerHeight}px`);

    // Desktop grow-over: the field's left edge stays put and it widens across the rest of
    // the right cluster, whose items fade out in place (their boxes keep the row width).
    const slot = this.header.querySelector('.header__search-slot');
    const cluster = slot?.closest('.header__right');
    if (!slot || !cluster || !this.desktopQuery.matches) return;

    const openWidth = cluster.getBoundingClientRect().right - slot.getBoundingClientRect().left;
    this.header.style.setProperty('--search-field-open-width', `${openWidth}px`);
  }

  // Wishlist, Account and Bag fade out under the widened field on desktop; `inert` takes
  // them out of the tab order and the accessibility tree at once.
  setHeaderIconsInert(isInert) {
    const items = this.header.querySelectorAll('.header__right > :not(.header__search-slot)');
    items.forEach((item) => {
      item.inert = isInert && this.desktopQuery.matches;
    });
  }

  // The page must not scroll behind the scrim, but hiding its scrollbar would shift the
  // right-aligned header (and the growing field) by the scrollbar's width, and padding the body
  // to compensate leaves an undimmed-header, dimmed-strip seam at the edge. So the scrollbar
  // stays and the scroll gestures are stopped instead: anywhere outside the panel, and inside
  // it once the panel has reached its end in that direction.
  lockPage() {
    document.addEventListener('wheel', this.onScrollAttempt, { passive: false });
    document.addEventListener('touchmove', this.onScrollAttempt, { passive: false });
  }

  unlockPage() {
    document.removeEventListener('wheel', this.onScrollAttempt, { passive: false });
    document.removeEventListener('touchmove', this.onScrollAttempt, { passive: false });
  }

  onScrollAttempt = (event) => {
    const panel = event.target.closest?.('[data-search-panel]');

    if (!panel) {
      if (event.cancelable) event.preventDefault();
      return;
    }

    if (event.type === 'touchmove') {
      // The explore row scrolls sideways on its own; a scrollable panel is kept from chaining
      // by `overscroll-behavior: contain`; a panel with nothing to scroll must not pass the
      // drag on to the page.
      if (event.target.closest('.search-overlay__collections')) return;
      if (panel.scrollHeight <= panel.clientHeight && event.cancelable) event.preventDefault();
      return;
    }

    if (event.deltaY === 0) return;

    const atTop = panel.scrollTop <= 0;
    const atBottom = Math.ceil(panel.scrollTop + panel.clientHeight) >= panel.scrollHeight;
    if ((event.deltaY < 0 && atTop) || (event.deltaY > 0 && atBottom)) event.preventDefault();
  };

  syncClearButtons() {
    document.querySelectorAll('[data-header-search-clear]').forEach((button) => {
      const input = button.closest('[data-header-search]')?.querySelector('[data-header-search-input]');
      const label = input && input.value !== '' ? button.dataset.labelClear : button.dataset.labelClose;
      if (label) button.setAttribute('aria-label', label);
    });
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  clearDebounce() {
    if (!this.debounceId) return;

    window.clearTimeout(this.debounceId);
    this.debounceId = 0;
  }

  clearCloseTimer() {
    if (!this.closeTimer) return;

    window.clearTimeout(this.closeTimer);
    this.closeTimer = 0;
  }
}

customElements.define('search-overlay', SearchOverlay);
